<template>
  <div
    class="timeline"
    style="position: relative"
  >
    <p style="max-width: 800px">
      This visualization highlights artists related to {{ artist.name }}. Each circle represents a song. The songs are organized into three major sections: level -1, level 0, and level 1. Level -1 contains songs that create abilities in which {{ artist.name }} is involved. Level 0 contains songs in which {{ artist.name }} is involved. Level 3 contains adaptations from a song in which {{ artist.name }} is involved.
      <br>
      <a
        role="button"
        @click="startTuto()"
      >
        <i class="mdi mdi-information-outline" /> Start the interactive tutorial.
      </a>
    </p>
        
    <br>

    <v-legend
      :artist="artist"
    />

    <v-search-song
      :songs="timeline.allSongsValues"
      @select="onSearch($event)"
    />

    <svg
      v-if="timeline"
      :width="timeline.width + timeline.margin.left + timeline.margin.right"
      :height="timeline.height + timeline.margin.top + timeline.margin.bottom"
    >
      <g :transform="`translate(${timeline.margin.left},${timeline.margin.top})`">

        <rect
          :width="timeline.width"
          :height="timeline.height"
          fill="transparent"
          @click="onSearch(null)"
        />

        <g
          v-for="(row, i) in timeline.rows"
          :id="`timeline-${i}`"
          :key="i"
          :transform="`translate(${-timeline.margin.left},${timeline.row.height * i})`"
          class="rows"
        >
          <rect
            :width="timeline.width"
            :height="timeline.row.height"
            fill="transparent"
          />
        </g>

        <g class="axis">
          <g
            v-for="(row, i) in timeline.rows"
            :id="`timeline-${i}`"
            :key="i"
            :transform="`translate(${-timeline.margin.left},${timeline.row.height * i})`"
          >
            <line 
              class="axis-x"
              :x1="timeline.past.width" 
              :x2="timeline.width + timeline.margin.left + timeline.margin.right" 
              :y1="timeline.row.height/2" 
              :y2="timeline.row.height/2"
              stroke="#f1f1f1"
              stroke-width="2"
            />
            <line 
              class="axis-cut"
              :x1="0" 
              :x2="timeline.past.width - 6" 
              :y1="timeline.row.height/2" 
              :y2="timeline.row.height/2"
              stroke="#f1f1f1"
              stroke-width="2"
              stroke-dasharray="5"
            />
            <line 
              class="axis-cut"
              :x1="timeline.past.width - 2" 
              :x2="timeline.past.width - 7" 
              :y1="timeline.row.height/2 - 10" 
              :y2="timeline.row.height/2 + 10"
              stroke="#ccc"
              stroke-width="1.5"
            />
            <line 
              class="axis-cut"
              :x1="timeline.past.width + 2" 
              :x2="timeline.past.width - 3" 
              :y1="timeline.row.height/2 - 10" 
              :y2="timeline.row.height/2 + 10"
              stroke="#ccc"
              stroke-width="1.5"
            />
            <text 
              class="label"
              :transform="`translate(0,${timeline.row.height/2 + 20})`"
              fill="#666"
              style="font-weight: 400; font-size: 0.9rem;"
            >
              Level {{ i - 1 }}
            </text>
          </g>
        </g>

        <g class="links">
          <g
            v-for="(link, i) in timeline.links"
            :key="i"
            class="link"
          >
            <path
              :d="timeline.diagonal({source: link.source, target: link.target})"
              fill="transparent"
              :opacity="pathOpacity(link)"
              :stroke="pathStroke(link)"
              :stroke-width="pathStrokeWidth(link)"
            />
          </g>
        </g>

        <g class="nodes">
          <g
            v-for="(row, i) in timeline.rows"
            :key="i"
            :class="`nodes-${i}`"
          >

            <circle
              v-for="song in row.nodes"
              :id="`node-${song.id}`"
              :key="song.id"
              class="node"
              :r="song.radius - 1.2"
              :cx="song.x"
              :cy="song.y"
              :fill="timeline.color(song.level)"
              :stroke="rgb(timeline.color(song.level)).darker(2)"
              :stroke-width="nodeStrokeWidth(song)"
              :opacity="nodeOpacity(song)"
              @mouseenter="onMouseover(song.id)"
              @mouseleave="onMouseover(null)"
              @click="onClick(song.id)"
            />
                        
          </g>
        </g>

        <g 
          v-axis="timeline.axis.x"
          class="axis x"
          :transform="`translate(0,${timeline.height - 20})`"
        />

      </g>

    </svg>
        
    <portal to="tooltip">
      <v-tooltip
        :song="hoveredSong"
        :artist="artist" 
      />
    </portal>

    <br>
    <br>

    <div class="columns">
      <div class="column">
        <v-artist-lists
          ref="lists"
          :artists="timeline.artists"
          :artist-id="artist.id"
          @mouseoverartist="onArtistMouseover"
          @mouseoverrole="onRoleMouseover"
        />
      </div>
    </div>
  </div>
</template>

<script>

import { Timeline } from "./timeline";
import { rgb, select } from "d3";
import variables from "@/assets/scss/_variables.scss";
import { toSong, toArtist } from "@/services/navigator";
import Tree from "./tree/Tree.vue";
import ArtistLists from "./ArtistLists.vue";
import Tooltip from "./Tooltip.vue";
import Legend from "./Legend.vue";
import SearchSong from "./SearchSong.vue";
import tuto from "./tuto";
import { setCookie, getCookie } from "@/services/helpers";

export default {
	components: {
		"v-tree": Tree,
		"v-artist-lists": ArtistLists,
		"v-tooltip": Tooltip,
		"v-legend": Legend,
		"v-search-song": SearchSong,
	},
	directives: {
		axis (el, binding) {
			let ax = select(el).call(binding.value);
			ax.call(g => g.selectAll(".axis .domain").remove());
			ax.call(g => g.selectAll(".axis .tick line").attr("stroke", variables["white-dark"]));
		}
	},
	props: {
		artist: {
			required: true,
			type: Object
		}
	},
	data () {
		return {
			allowInteraction: true,
			activePanel: 0,
			timeline: null,
			mouseover: null,
			searchedSong: null,
			selectedSong: null,
			roleMouseover: null,
			artistMouseover: {
				id: null,
				level: null
			}
		};
	},
	computed: {
		hoveredSong () {
			return this.$store.state.songs[this.mouseover];
		},
		hasSeenTuto() {
			return getCookie("timeline-tuto") == "true" || false;
		}
	},
	mounted () {
		this.initTuto();
	},
	created () {
		this.timeline = new Timeline(this.artist);

		this.rgb = rgb;
	},
	methods: {
		initTuto() {
			if (!this.hasSeenTuto) {       
				this.startTuto();
			}
		},
		startTuto () {       
			tuto(this);
			setCookie("timeline-tuto", true, 10);
		},
		toArtist (artist) {
			toArtist(artist.id);
		},
		nodeSearched (song) {
			return this.searchedSong == song.id;
		},
		nodeSelected (song) {
			return this.selectedSong == song.id;
		},
		nodeHover (song) {
			return (
				this.mouseover == song.id || 
        this.mouseover == song.parent_id || 
        (this.mouseover != null && this.$store.state.songs[this.mouseover].parent_id == song.id) ||
        (song.artist_ids.includes(this.artistMouseover.id) && song.level == this.artistMouseover.level && this.roleMouseover == null) ||
        (song.artist_ids.includes(this.artistMouseover.id) && song.level == this.artistMouseover.level && song[this.roleMouseover + "_ids"].includes(this.artistMouseover.id))
			);
		},
		nodeStrokeWidth (song) {
			if (this.nodeHover(song) || this.nodeSearched(song)) {
				return 2;
			}
			if (this.nodeSelected(song)) {
				return 1.2;
			}
			return 1;
		},
		nodeOpacity (song) {
			if (this.nodeHover(song)) {
				return 1;
			}
			if (this.nodeSelected(song)) {
				return 1;
			}
			return 0.7;
		},
		pathSelected (link) {
			return (
				this.selectedSong == link.source.id || 
        this.selectedSong == link.target.id
			);
		},
		pathHover (link) {
			return (
				this.searchedSong == link.source.id || 
				this.searchedSong == link.target.id || 
				this.mouseover == link.source.id || 
        this.mouseover == link.target.id /*|| 
         (link.target.artist_ids.includes(this.artistMouseover.id) && (this.artistMouseover.level == "target") && (this.roleMouseover == null)) || 
        (link.target.artist_ids.includes(this.artistMouseover.id) && (this.artistMouseover.level == "target") && link.target[this.roleMouseover + "_ids"].includes(this.artistMouseover.id)) || 
        (link.source.artist_ids.includes(this.artistMouseover.id) && (this.artistMouseover.level == "source") && (this.roleMouseover == null)) ||
        (link.source.artist_ids.includes(this.artistMouseover.id) && (this.artistMouseover.level == "source") && link.source[this.roleMouseover + "_ids"].includes(this.artistMouseover.id))
      */
			);
		},
		pathOpacity (link) {
			if (this.pathHover(link)) {
				return 1;
			}
			if (this.pathSelected(link)) {
				return 0.7;
			}
			return 0.1;
		},
		pathStroke (link) {
			if (link.source.artist_ids.includes(this.artist.id)) {
				return this.timeline.colors.target;
			} 
			return this.timeline.colors.source;
		},
		pathStrokeWidth (link) {
			if (this.pathHover(link)) {
				return 2;
			}
			if (this.pathSelected(link)) {
				return 1.2;
			}
			return 1;
		},
		onSearch (value) {
			this.searchedSong = value;
			this.selectedSong = value;
		},
		onClick (value) {
			if (this.allowInteraction) {
				this.searchedSong = null;
				this.selectedSong = value;
				toSong(value);
			}
		},
		onMouseover (value) {
			if (this.allowInteraction) {
				this.mouseover = value;
			}
		},
		onArtistMouseover (payload) {
			if (this.allowInteraction) {
				this.artistMouseover = payload;
			}
		},
		onRoleMouseover (payload) {
			if (this.allowInteraction) {
				this.roleMouseover = payload;
			}
		}
	}
};
</script>
